import { toNumber } from 'lodash-es';
import { useCallback, useRef, useState } from 'react';

import type { ChartLayoutDimensions } from '../components/chart-layout/types/chart-layout.js';

/**
 * Calculates the dimensions of the chart layout container including paddings.
 * @param node -
 */
function calculateNodeDimensions(node: HTMLDivElement): ChartLayoutDimensions {
  const getNodePaddings = (node: HTMLDivElement) => {
    const coercePadding = (padding: string) =>
      toNumber(padding.match(/^\d+/g)?.join('') ?? 0);
    const computedElStyle = getComputedStyle(node);

    return {
      top: coercePadding(computedElStyle.paddingTop),
      bottom: coercePadding(computedElStyle.paddingBottom),
      left: coercePadding(computedElStyle.paddingLeft),
      right: coercePadding(computedElStyle.paddingRight),
    };
  };

  const { width, height } = node.getBoundingClientRect();
  const padding = getNodePaddings(node);
  const { height: wrapperHeight } =
    node.parentElement?.getBoundingClientRect() ?? { height: 0 };

  return {
    width: width - padding.left - padding.right,
    height: height - padding.top - padding.bottom,
    padding,
    wrapperHeight: wrapperHeight - padding.top - padding.bottom,
  };
}

/**
 * Hook to observe the dimensions of the referenced Div element.
 * @internal
 */
export const useObserveContainerSize = (): [
  ChartLayoutDimensions,
  (node: HTMLDivElement) => void,
] => {
  const [dimensions, setDimensions] = useState<ChartLayoutDimensions>({
    width: 0,
    height: 0,
    padding: { top: 0, bottom: 0, left: 0, right: 0 },
    wrapperHeight: 0,
  });
  const observerCleanupRef = useRef<() => void>(undefined);

  const ref = useCallback((node: HTMLDivElement) => {
    if (node === null) {
      if (observerCleanupRef.current) {
        observerCleanupRef.current();
      }
      return;
    }
    setDimensions(calculateNodeDimensions(node));

    const observer = new ResizeObserver(() => {
      setDimensions(calculateNodeDimensions(node));
    });

    observerCleanupRef.current = () => observer.unobserve(node);
    observer.observe(node);
  }, []);

  return [dimensions, ref];
};
